import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

import api from "../../services/api";
import { AuthContext } from "../Auth/AuthContext";

const KEY = "numera.activeProjectId";

const ProjectContext = createContext({
  projects: [],
  activeProjectId: null,
  setActiveProjectId: () => {},
  reloadProjects: async () => {},
});

export const ProjectProvider = ({ children }) => {
  const { isAuth } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActive] = useState(() => {
    const v = localStorage.getItem(KEY);
    return v ? Number(v) : null;
  });

  const setActiveProjectId = useCallback(id => {
    setActive(id);
    if (id) localStorage.setItem(KEY, String(id));
    else localStorage.removeItem(KEY);
  }, []);

  const reloadProjects = useCallback(async () => {
    const { data } = await api.get("/projects");
    const list = data.projects || data || [];
    setProjects(list);
    return list;
  }, []);

  // load projects once authenticated (the access token + api interceptors are only
  // ready after auth settles); ensure there's always an active project selected
  useEffect(() => {
    if (!isAuth) return;
    (async () => {
      try {
        const list = await reloadProjects();
        setActive(prev => {
          if (prev && list.some(p => p.id === prev)) return prev;
          // no stored selection: prefer the default project, then the first
          const fallback = list.find(p => p.isDefault) || list[0];
          const id = fallback ? fallback.id : null;
          if (id) localStorage.setItem(KEY, String(id));
          return id;
        });
      } catch {
        /* ignore */
      }
    })();
  }, [isAuth, reloadProjects]);

  return (
    <ProjectContext.Provider
      value={{ projects, activeProjectId, setActiveProjectId, reloadProjects }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => useContext(ProjectContext);

export default ProjectContext;
